import _ from 'underscore';
import React, { useCallback, useMemo, useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withValidation, GenericAdapter } from '@bingads-webui-react/validation';
import { withErrorField } from '@bingads-webui-react/primitive-validation-fluent';
import { mergeClasses } from '@fluentui/react-components';

import { BudgetInputPure } from './budget-input-pure';
import { useBudgetRange } from './use-budget-range';
import { useValidatedBudgetInputStyles } from './styles/validated-budget-input-styles';

const BudgetInput = withErrorField(withValidation(BudgetInputPure, GenericAdapter));

declare function _TL_(arg0: string): string;

interface ValidatedBudgetInputProps {
  budgetKey?: string;
  maxBudget?: number;
  minBudget?: number;
  currency?: string;
  validationProps: {},
}

export const ValidatedBudgetInput: React.FC<ValidatedBudgetInputProps> = (props) => {
  const {
    currency,
    budgetKey,
    maxBudget,
    minBudget,
    validationProps,
    ...rest
  } = props;

  const classes = useValidatedBudgetInputStyles();
  const i18n = useContext(I18nContext) as any;

  const {
    currencyCode,
    minDailyBudget,
    maxDailyBudget,
  } = useBudgetRange();

  const finalMaxBudget = maxBudget || maxDailyBudget;
  const finalMinBudget = minBudget || minDailyBudget;
  const finalCurrency = currency || currencyCode;

  const budgetSchema = useMemo(() => ({
    name: 'Budget schema',
    type: 'object',
    properties: {
      [String(budgetKey)]: {
        type: 'number',
        required: true,
        maximum: finalMaxBudget,
        minimum: finalMinBudget,
      },
    },
  }), [budgetKey, finalMaxBudget, finalMinBudget]);

  const customErrorProcessor = useCallback(errors => _.map(
    errors,
    (oldError, key) => {
      const customErrors = {
        decimalLength: i18n.getString(_TL_('Budget needs to be set to 2 decimal lengths')),
        range: i18n.getString(_TL_('Your budget should be greater than {{accountCurrencyCode}} {{dailyRangeFloorValue}} and less than your daily maximum. The daily max. is your monthly budget averaged out over the number of days left in the month.'), {
          accountCurrencyCode: finalCurrency,
          dailyRangeFloorValue: finalMinBudget,
        }),
        required: i18n.getString(_TL_('This field is required')),
        type: i18n.getString(_TL_('Field requires numeric value')),
      };
      return customErrors[key] || oldError;
    }
  ), [finalCurrency, finalMinBudget, i18n]);

  return (
    <div className={mergeClasses('validated-budget-input-container', classes.root)}>
      <BudgetInput
        i18n={i18n}
        validationProps={{
          schema: budgetSchema,
          field: budgetKey,
          errorProcessor: customErrorProcessor,
          ...validationProps,
        }}
        currency={finalCurrency}
        key={`${finalCurrency}-${finalMaxBudget}-${finalMinBudget}`}
        {...rest}
      />
    </div>
  );
};

ValidatedBudgetInput.defaultProps = {
  budgetKey: 'Budget',
  maxBudget: undefined,
  minBudget: undefined,
  currency: undefined,
};
